import React from 'react';
import {
	FormControl,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
	Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EdgeType from '../../../../../enums/edgeType';

interface EdgeTypeSelectorProps {
	edgeTypes: EdgeType[];
	value: EdgeType | undefined;
	onChange: (value: EdgeType) => void;
	disabled?: boolean;
	label?: string;
	onDropdownOpenChange?: (open: boolean) => void;
}

const EdgeTypeSelector: React.FC<EdgeTypeSelectorProps> = ({
	edgeTypes,
	value,
	onChange,
	disabled = false,
	label = 'Edge Types',
	onDropdownOpenChange,
}) => {
	const theme = useTheme();
	const [isOpen, setIsOpen] = React.useState(false);

	const handleChange = (event: SelectChangeEvent<EdgeType>) => {
		onChange(event.target.value as EdgeType);
	};

	const handleOpen = () => {
		setIsOpen(true);
		onDropdownOpenChange?.(true);
	};

	const handleClose = () => {
		setIsOpen(false);
		onDropdownOpenChange?.(false);
	};

	const selectId = 'edge-type-select';
	const labelId = `${selectId}-label`;

	return (
		<Box>
			<Typography
				variant="body2"
				sx={{
					fontWeight: 500,
					color: theme.palette.text.primary,
					mb: theme.spacing(0.5),
				}}
			>
				{label}
			</Typography>
			<FormControl fullWidth size="small" disabled={disabled}>
				<Select
					id={selectId}
					value={value || ''}
					onChange={handleChange}
					disabled={disabled}
					displayEmpty
					open={isOpen}
					onOpen={handleOpen}
					onClose={handleClose}
					MenuProps={{
						disablePortal: false,
						anchorOrigin: {
							vertical: 'bottom',
							horizontal: 'left',
						},
						transformOrigin: {
							vertical: 'top',
							horizontal: 'left',
						},
						PaperProps: {
							style: {
								maxHeight: 240,
								marginTop: 4,
							},
						},
						slotProps: {
							root: {
								style: {
									zIndex: 99999,
								},
							},
						},
					}}
					sx={{
						'& .MuiSelect-select': {
							borderRadius: theme.shape.borderRadius,
						},
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor:
								theme.palette.secondary?.[300] || theme.palette.grey[300],
						},
						'&:hover .MuiOutlinedInput-notchedOutline': {
							borderColor:
								theme.palette.secondary?.[400] || theme.palette.grey[400],
						},
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: theme.palette.primary.main,
						},
					}}
				>
					{edgeTypes.map((edgeType, index) => (
						<MenuItem
							key={index}
							value={edgeType}
							sx={{
								fontSize: 'sm',
								borderRadius: theme.shape.borderRadius,
								mx: theme.spacing(0.5),
								'&:hover': {
									backgroundColor: theme.palette.action.hover,
								},
								'&.Mui-selected': {
									backgroundColor: theme.palette.primary.subtle,
									'&:hover': {
										backgroundColor: theme.palette.primary.subtle,
										filter: 'contrast(1.05)',
									},
								},
							}}
						>
							{edgeType}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Box>
	);
};

export default EdgeTypeSelector;
